import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@glu/theming';
import get from 'lodash/get';
import { Button, PRIMARY, TERTIARY } from '@glu/buttons-react';
import { JSSClasses } from '../../proptypes/jss-prop-types';
import Modal from '../modalNotification/ModalNotification';
import withLocale from '../withLocale/withLocale';
import styles from './GridActionModal.styles';

export const modifyActions = (actions, type, setActiveAction, setData) => {
  if (typeof actions === 'function') return actions;

  return actions.map((action) => {
    if (!action.template) {
      return action;
    }

    return {
      ...action,
      handler: (...args) => {
        if (type === 'row') {
          const [{ data }] = args;
          setData(data);
          setActiveAction(`${action.label}-${type}`);
          return;
        }
        const [bulkModels] = args;
        setData(bulkModels);
        setActiveAction(`${action.label}-${type}`);
      },
      modalAction: (data) => {
        setActiveAction('');
        action.handler(data);
      }
    };
  });
};

export const modifyColumns = (columnDefs, setActiveAction, setData) =>
  columnDefs.map((column) => {
    if (!column.actions) {
      return column;
    }
    const actions = modifyActions(column.actions, 'row', setActiveAction, setData);
    return {
      ...column,
      actions
    };
  });

export function findActive(modifiedBulkActions, modifiedColumns, activeAction) {
  const active = modifiedBulkActions.find(
    (bulkAction) => `${bulkAction.label}-bulk` === activeAction
  );
  if (active) {
    return active;
  }
  const rowButtons = modifiedColumns
    .filter((column) => column.actions)
    .filter((column) => typeof column.actions !== 'function')
    .reduce((actions, column) => [...actions, ...column.actions], []);

  const activeRowButton = rowButtons.find(
    (rowButton) => `${rowButton.label}-row` === activeAction
  );
  if (activeRowButton) {
    return activeRowButton;
  }
  return false;
}

export function GridActionModalComponent({
  bulkActions,
  children,
  classes: { gridActionModalWrapper },
  columnDefs,
  exitBtnLabel,
  locale,
  noModal,
  proceedBtnLabel
}) {
  const [activeAction, setActiveAction] = useState(false);
  const [data, setData] = useState([]);

  const hasRowButtons = useMemo(() => columnDefs.some((column) => column.actions), [columnDefs]);

  const modifiedBulkActions = useMemo(
    () => modifyActions(bulkActions, 'bulk', setActiveAction, setData),
    [bulkActions]
  );
  const modifiedColumns = useMemo(
    () => modifyColumns(columnDefs, setActiveAction, setData),
    [columnDefs]
  );
  const active = useMemo(
    () => findActive(modifiedBulkActions, modifiedColumns, activeAction),
    [modifiedBulkActions, modifiedColumns, activeAction]
  );
  const renderModalContent = useMemo(
    () => active.template && active.template(data),
    [active, data]
  );

  const activeProps = get(active, 'templateProps');

  const onModalHide = useCallback(() => setActiveAction(''), []);
  const onModalProceed = useCallback(() => {
    active.modalAction(data);
  }, [active, data]);
  const onExitModal = useCallback(() => setActiveAction(''), []);

  if ((!hasRowButtons && !bulkActions.length) || noModal) {
    return children({});
  }

  return (
    <div className={gridActionModalWrapper}>
      <Modal
        onHide={onModalHide}
        show={!!activeAction}
        suppressIcon={false}
        variant="error"
        {...activeProps}
      >
        {renderModalContent}
        <Button
          dataQa="proceed-grid-modal-btn"
          className="btn btn-primary"
          variant={PRIMARY}
          onClick={onModalProceed}
        >
          {proceedBtnLabel || locale['common.proceed']}
        </Button>
        <Button
          dataQa="exit-grid-modal-btn"
          className="btn btn-tertiary"
          variant={TERTIARY}
          onClick={onExitModal}
        >
          {exitBtnLabel || locale['common.exit']}
        </Button>
      </Modal>
      {children({
        modifiedBulkActions,
        modifiedColumns
      })}
    </div>
  );
}

GridActionModalComponent.propTypes = {
  classes: JSSClasses,
  bulkActions: PropTypes.arrayOf(
    PropTypes.shape({
      handler: PropTypes.func,
      label: PropTypes.string
    })
  ),
  children: PropTypes.func.isRequired,
  columnDefs: PropTypes.arrayOf(PropTypes.shape({})),
  exitBtnLabel: PropTypes.string,
  locale: PropTypes.objectOf(PropTypes.string),
  noModal: PropTypes.bool,
  proceedBtnLabel: PropTypes.string
};

GridActionModalComponent.defaultProps = {
  classes: {},
  bulkActions: [],
  columnDefs: [],
  exitBtnLabel: '',
  locale: {
    'common.exit': 'Exit',
    'common.proceed': 'Proceed'
  },
  noModal: false,
  proceedBtnLabel: ''
};
const Styled = withStyles(styles)(GridActionModalComponent);
export default withLocale(Styled);
